import type { FirebaseSdk, FirestoreSdk } from '~/types/plugins'

/**
 * Firebase (app, auth, functions ~170 kB) is loaded on first use, not with every page (mobiilinopeus 26.10.2026).
 * Only signing in, the account pages, checkout and a logged-in visitor's cart calls need it. For a logged-in
 * visitor it is loaded right after the page is ready, so the session is restored by the time it is needed.
 */
export default defineNuxtPlugin((nuxtApp) => {
  const config = useRuntimeConfig()
  const firebaseConfig = config.public.firebase
  const useEmulators = import.meta.dev && config.public.FIREBASE_EMULATORS

  let firebase: Promise<FirebaseSdk> | null = null
  const loadFirebase = (): Promise<FirebaseSdk> => {
    firebase ??= Promise.all([import('firebase/app'), import('firebase/auth'), import('firebase/functions')])
      .then(([appSdk, authSdk, functionsSdk]) => {
        const app = appSdk.initializeApp(firebaseConfig)
        const functions = functionsSdk.getFunctions(app, 'europe-north1')
        // initializeAuth instead of getAuth: without a popupRedirectResolver the SDK does not load the
        // auth iframe (firebaseapp.com/__/auth/iframe.js, ~95 kB). The site signs in with email and password
        // only; add browserPopupRedirectResolver here if popup/redirect sign-in is ever needed.
        const auth = authSdk.initializeAuth(app, { persistence: [authSdk.indexedDBLocalPersistence, authSdk.browserLocalPersistence] })

        if (useEmulators) {
          authSdk.connectAuthEmulator(auth, 'http://127.0.0.1:9099', { disableWarnings: process.env.NODE_ENV === 'development' })
          functionsSdk.connectFunctionsEmulator(functions, 'localhost', 5002)
        }
        return { app, auth, functions, authSdk, functionsSdk }
      })
    // A failed chunk load (e.g. offline) may be retried on the next call
    firebase.catch(() => {
      firebase = null
    })
    return firebase
  }

  // Firestore (~270 kB) is loaded on first use: only the account pages and wishlists write to it
  let firestore: Promise<FirestoreSdk> | null = null
  const loadFirestore = (): Promise<FirestoreSdk> => {
    firestore ??= Promise.all([loadFirebase(), import('firebase/firestore')]).then(([{ app }, sdk]) => {
      const db = sdk.getFirestore(app)
      if (useEmulators) {
        sdk.connectFirestoreEmulator(db, 'localhost', 9090)
      }
      return { ...sdk, db }
    })
    return firestore
  }

  onNuxtReady(() => {
    if (useUserStore().loggedIn) loadFirebase()
  })

  nuxtApp.vueApp.provide('firebase', loadFirebase)
  nuxtApp.provide('firebase', loadFirebase)

  nuxtApp.vueApp.provide('firestore', loadFirestore)
  nuxtApp.provide('firestore', loadFirestore)
})
